import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import { retryNow } from '../../services/connectivity.service';
import WifiConnectionOffIcon from '../../assets/bombiix-icons/WifiConnectionOffIcon';
import WifiConnectionOnIcon from '../../assets/bombiix-icons/WifiConnectionOnIcon';
import './OfflineBanner.css';

const BACK_ONLINE_VISIBLE_MS = 4000;
const BAR_VISIBLE_CLASS = 'has-connection-bar';

/**
 * Fixed full-width connection bar (Figma 16924:257925 offline, 16924:258077 back online).
 * While the app has no usable connection it shows a red "you are offline" bar with a Retry
 * action; once the connection returns it shows a green "back online" confirmation for 4s.
 * While either bar is visible it flags the root element so the page layout moves down by the
 * bar's height instead of the bar covering the main menu.
 * @returns The connection bar, or null while the connection is healthy.
 */
function OfflineBanner(): React.ReactElement | null {
  const isOnline = useOnlineStatus();
  const [showBackOnline, setShowBackOnline] = useState(false);
  const wasOffline = useRef(false);
  const { t } = useTranslation('common');
  const isVisible = !isOnline || showBackOnline;

  useEffect(() => {
    if (!isOnline) {
      wasOffline.current = true;
      setShowBackOnline(false);
      return undefined;
    }
    if (!wasOffline.current) {
      return undefined;
    }
    wasOffline.current = false;
    setShowBackOnline(true);
    const timer = setTimeout(() => setShowBackOnline(false), BACK_ONLINE_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [isOnline]);

  useEffect(() => {
    document.documentElement.classList.toggle(BAR_VISIBLE_CLASS, isVisible);
    return () => {
      document.documentElement.classList.remove(BAR_VISIBLE_CLASS);
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  if (isOnline) {
    return (
      <div className="offline-banner offline-banner--online" role="status" aria-live="polite">
        <span className="offline-banner__icon">
          <WifiConnectionOnIcon />
        </span>
        <span className="offline-banner__text">{t('OFFLINE_BANNER.BACK_ONLINE', { defaultValue: 'You are back online.' })}</span>
      </div>
    );
  }

  return (
    <div className="offline-banner" role="alert" aria-live="assertive">
      <span className="offline-banner__icon">
        <WifiConnectionOffIcon />
      </span>
      <span className="offline-banner__text">
        {t('OFFLINE_BANNER.MESSAGE', { defaultValue: 'You are offline. Please check your internet connection.' })}
      </span>
      <button type="button" className="offline-banner__retry" onClick={() => retryNow()}>
        {t('OFFLINE_BANNER.RETRY', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}

export default React.memo(OfflineBanner);
